import { useTranslation } from '../../../i18n';
import { Card } from '../../../components/Shared/Card';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from '../../../components/Shared/Table';
import { formatDate } from '@/utils/dateUtils';
import { formatMoney } from '@/utils/money';
import type { FacilityFlowCoreModulesInvoicesDtosStandingInstallmentDto } from '../../../api/generated/models';

interface StandingPaymentPlanProps {
  installments: FacilityFlowCoreModulesInvoicesDtosStandingInstallmentDto[];
  currency?: string | null;
}

/**
 * Payment plan of a standing invoice (FAFD-27): one row per payment with due date, period and
 * amounts. The last payment of each service year takes the rounding difference — marked with „*“.
 */
export function StandingPaymentPlan({ installments, currency }: StandingPaymentPlanProps) {
  const { t } = useTranslation('invoices');
  const money = (value?: number) => formatMoney(value, { currency: currency ?? undefined });
  const hasAdjustment = installments.some((i) => i.isRoundingAdjustment);

  return (
    <Card title={t('invoices:paymentPlan.title')} noPadding>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader className="w-16">{t('invoices:paymentPlan.number')}</TableHeader>
            <TableHeader>{t('invoices:paymentPlan.dueDate')}</TableHeader>
            <TableHeader className="hidden sm:table-cell">{t('invoices:paymentPlan.period')}</TableHeader>
            <TableHeader className="text-right">{t('invoices:paymentPlan.net')}</TableHeader>
            <TableHeader className="hidden md:table-cell text-right">{t('invoices:paymentPlan.vat')}</TableHeader>
            <TableHeader className="text-right">{t('invoices:paymentPlan.gross')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {installments.map((i) => (
            <TableRow key={i.number}>
              <TableCell className="text-[13px] text-slate-700 tabular-nums">
                {i.number}
                {i.isRoundingAdjustment && <span className="ml-0.5 text-slate-500">*</span>}
              </TableCell>
              <TableCell className="text-[13px] text-slate-700 tabular-nums whitespace-nowrap">
                {formatDate(i.dueDate)}
              </TableCell>
              <TableCell className="hidden sm:table-cell text-[13px] text-slate-700 tabular-nums whitespace-nowrap">
                {formatDate(i.periodFrom)} – {formatDate(i.periodTo)}
              </TableCell>
              <TableCell className="text-right text-[13px] text-slate-900 tabular-nums">{money(i.netAmount)}</TableCell>
              <TableCell className="hidden md:table-cell text-right text-[13px] text-slate-700 tabular-nums">
                {money(i.vatAmount)}
              </TableCell>
              <TableCell className="text-right text-[13px] font-medium text-slate-900 tabular-nums">
                {money(i.grossAmount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasAdjustment && (
        <p className="px-4 py-2.5 border-t border-slate-100 text-[11px] text-slate-500">
          {t('invoices:paymentPlan.adjustmentFootnote')}
        </p>
      )}
    </Card>
  );
}
